import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, storeAuth, clearAuth, loadUser } from "./api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(loadUser());
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      storeAuth({ user: data });
    } catch {
      setUser(null);
      clearAuth();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const signin = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    storeAuth({ token: data.token, user: data.user });
    setUser(data.user);
    return data.user;
  };

  const signup = async (email, password, name) => {
    const { data } = await api.post("/auth/signup", { email, password, name });
    storeAuth({ token: data.token, user: data.user });
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    clearAuth();
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, signin, signup, logout, refresh, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
